import { ParentContact } from '../../entities/contact/contact.options';
import { ContactType } from '../../entities/contact/contact.type';
import { SecurityRoleTiny } from '../security.role';
import { SecurityRoleUtility } from '../utility/security.roles.utility';
import { crmRecordKindOf, crmRecordRight } from './crm.record.rights';
import { CrmSecurityRight } from './crm.security.module';
import { JobSectionVerb } from './job.section.rights';

/** The text the web app already shows for a refused CRM write, so its toasts stay as they are. */
export const CRM_RECORD_REFUSED = 'You do not have permission to perform this action';

/**
 * Whether the role may act on a record of this contact type. No role means an internal call with no signed-in user
 * (workers, pipelines), which these rights never governed; a type with no CRM row (site, equipment, system) or an
 * action the row does not offer (company create / delete) is not decided here.
 */
export function canActOnCrmRecord(role: SecurityRoleTiny | null | undefined, type: ContactType | string | undefined, verb: JobSectionVerb): boolean {

    if (!role) return true;

    const kind = crmRecordKindOf(type);
    const right = kind ? crmRecordRight(kind, verb) : undefined;

    return right === undefined || SecurityRoleUtility.checkTinyRights(role, right);
}

/**
 * Contacts are details of the record that holds them, so changing one needs Edit on that record's kind; a contact
 * held by several records needs it on any one of them (or on the named one). Read from the stored parents, never the
 * request body. A contact whose holders have no CRM row (a site's contact) is not decided here.
 */
export function canEditContactOf(role: SecurityRoleTiny | null | undefined, parents: ParentContact[] | undefined, parentId?: string): boolean {

    if (!role) return true;

    const holders = (parents ?? []).filter(parent => !parentId || parent?._id === parentId);
    const rights = holders
        .map(parent => crmRecordKindOf(parent?.type))
        .filter(kind => !!kind)
        .map(kind => crmRecordRight(kind, 'edit'));

    return !rights.length || rights.some(right => SecurityRoleUtility.checkTinyRights(role, right));
}

const teamIds = (parents: ParentContact[] | undefined) => (parents ?? []).filter(parent => parent?.type === ContactType.Team).map(parent => parent._id).sort().join();
const otherParents = (parents: ParentContact[] | undefined) => (parents ?? []).filter(parent => parent?.type !== ContactType.Team).map(parent => parent?._id).sort().join();

/** Team membership is stored on the employee, so adding or removing a member is an employee patch of its parents. */
export function changesTeamMembership(stored: ParentContact[] | undefined, patch: object): boolean {

    const parents = (patch as { parents?: unknown })?.parents;

    return Array.isArray(parents) && teamIds(stored) !== teamIds(parents as ParentContact[]);
}

/** The Members tab sends `{ _id, parents }` with only Team entries changed; that is a team edit, not an employee edit. */
export function changesOnlyTeamMembership(stored: ParentContact[] | undefined, patch: object): boolean {

    const keys = Object.keys(patch ?? {}).filter(key => key !== '_id');

    return keys.length === 1
        && keys[0] === 'parents'
        && changesTeamMembership(stored, patch)
        && otherParents(stored) === otherParents((patch as { parents?: ParentContact[] }).parents);
}

/**
 * A patch of a stored record: the record's own Edit (`canEditStored`, decided by the caller), except that an employee
 * patch changing only Team entries is a team edit; any team change needs Teams Edit, any rate write View Financial Details,
 * and a `type` that moves the record to another kind needs Edit on that kind too (a type with no CRM row is refused).
 */
export function canPatchCrmRecord(role: SecurityRoleTiny | null | undefined, stored: { type?: ContactType | string; parents?: ParentContact[] }, patch: object, canEditStored: boolean): boolean {

    if (!role) return true;

    const teamsOnly = stored.type === ContactType.Employee && changesOnlyTeamMembership(stored.parents, patch);

    return (teamsOnly || canEditStored)
        && (!changesTeamMembership(stored.parents, patch) || canActOnCrmRecord(role, ContactType.Team, 'edit'))
        && (!touchesHourlyCostRate(patch) || canSeeFinancialDetails(role))
        && canMoveToKindOf(role, stored.type, (patch as { type?: string })?.type);
}

/** The record's own kind is not a write right to give it another one: the target kind must exist and be editable. */
function canMoveToKindOf(role: SecurityRoleTiny, storedType: ContactType | string | undefined, patchType: string | undefined): boolean {

    if (patchType === undefined || crmRecordKindOf(patchType) === crmRecordKindOf(storedType)) return true;

    return !!crmRecordKindOf(patchType) && canActOnCrmRecord(role, patchType, 'edit');
}

/** The rate can arrive as a nested timesheet or a dotted path; either way it is a write of the hidden rate. */
export function touchesHourlyCostRate(patch: object): boolean {

    const timesheet = (patch as { timesheet?: object })?.timesheet;

    return Object.prototype.hasOwnProperty.call(patch ?? {}, 'timesheet.hourlyCostRate')
        || (!!timesheet && Object.prototype.hasOwnProperty.call(timesheet, 'hourlyCostRate'));
}

/** A new record that arrives with a rate value; an empty rate on create sets nothing, so it is not a rate write. */
export function setsHourlyCostRate(item: object): boolean {

    const rate = (item as { timesheet?: { hourlyCostRate?: unknown } })?.timesheet?.hourlyCostRate ?? (item as Record<string, unknown>)?.['timesheet.hourlyCostRate'];

    return rate !== undefined && rate !== null && rate !== '';
}

export function canSeeFinancialDetails(role: SecurityRoleTiny | null | undefined): boolean {

    return !role || SecurityRoleUtility.checkTinyRights(role, CrmSecurityRight.ViewFinancialDetails);
}
